import React from "react";
import { Row, Col } from "react-bootstrap";
import { LuMapPin, LuGlobe } from "react-icons/lu";
import { DeliveryPartner } from "../types";

interface IntegrationCatalogProps {
  partners: DeliveryPartner[];
  onConnectPartner: (partner: DeliveryPartner) => void;
  onManagePartner: (partner: DeliveryPartner) => void;
}

const IntegrationCatalog: React.FC<IntegrationCatalogProps> = ({
  partners,
  onConnectPartner,
  onManagePartner,
}) => {
  return (
    <div className="bg-white rounded-4 p-4 border shadow-sm">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="fs-4 fw-bold text-dark mb-1">Integration</h2>
          <p className="text-muted small mb-0">
            Connect and manage automated last-mile courier & logistics partners for your store.
          </p>
        </div>
      </div>

      <Row className="g-4">
        {partners.map((partner) => {
          const isConnected = Boolean(
            partner.is_connected ||
              partner.connection_status === "connected" ||
              partner.connection_status === "pending_kyc" ||
              partner.status === "Connected"
          );

          return (
            <Col key={partner.id} lg={4} md={6} sm={12}>
              <div
                className="bg-white rounded-4 p-4 border h-100 d-flex flex-column justify-content-between transition-all"
                style={{
                  boxShadow: "0 2px 12px rgba(16, 24, 40, 0.03)",
                  borderColor: isConnected ? "#cbd5e1" : "#e2e8f0",
                }}
              >
                <div>
                  {/* Card Header: Icon/Logo & Status Badge */}
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    {partner.logo_url ? (
                      <img
                        src={partner.logo_url}
                        alt={partner.name}
                        className="rounded-circle object-fit-contain"
                        style={{ width: "44px", height: "44px" }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: "44px",
                          height: "44px",
                          backgroundColor:
                            partner.integration_key === "EVEX_DELIVERY" || partner.id === "evex"
                              ? "#dcfce7"
                              : partner.id === "ncm"
                                ? "#ffedd5"
                                : partner.id === "aramex"
                                  ? "#fee2e2"
                                  : "#e0f2fe",
                          color:
                            partner.integration_key === "EVEX_DELIVERY" || partner.id === "evex"
                              ? "#16a34a"
                              : partner.id === "ncm"
                                ? "#ea580c"
                                : partner.id === "aramex"
                                  ? "#dc2626"
                                  : "#0284c7",
                          fontWeight: 700,
                          fontSize: "14px",
                        }}
                      >
                        {partner.integration_key === "EVEX_DELIVERY" || partner.id === "evex" ? (
                          <span className="rounded-circle bg-success" style={{ width: 12, height: 12 }} />
                        ) : partner.id === "ncm" ? (
                          "NCM"
                        ) : partner.id === "aramex" ? (
                          <span style={{ fontSize: "11px", fontWeight: 800 }}>aramex</span>
                        ) : (
                          partner.name.substring(0, 2).toUpperCase()
                        )}
                      </div>
                    )}

                    {isConnected ? (
                      <span
                        className="badge px-3 py-2 fw-medium rounded-pill d-inline-flex align-items-center gap-1"
                        style={{ backgroundColor: "#ecfdf5", color: "#059669", fontSize: "12px" }}
                      >
                        <span className="rounded-circle bg-success" style={{ width: 6, height: 6 }} />
                        Connected
                      </span>
                    ) : (
                      <span
                        className="badge px-3 py-2 fw-medium rounded-pill"
                        style={{ backgroundColor: "#eff6ff", color: "#2563eb", fontSize: "12px" }}
                      >
                        Available
                      </span>
                    )}
                  </div>

                  {/* Partner Name & Subtitle */}
                  <h3 className="fs-5 fw-bold text-dark mb-1">{partner.name}</h3>
                  <div className="d-flex align-items-center gap-1 text-muted small mb-3">
                    {partner.locationType === "International" || partner.coverage?.toLowerCase().includes("global") ? (
                      <LuGlobe size={14} className="text-secondary flex-shrink-0" />
                    ) : (
                      <LuMapPin size={14} className="text-secondary flex-shrink-0" />
                    )}
                    <span>{partner.coverage || partner.subtitle || "Nationwide (Nepal)"}</span>
                  </div>

                  {/* Partner Description */}
                  {partner.description && (
                    <p className="text-muted small mb-3" style={{ fontSize: "13px", lineHeight: "1.4" }}>
                      {partner.description}
                    </p>
                  )}

                  {/* Tags */}
                  <div className="d-flex flex-wrap gap-2 mb-4">
                    {(partner.tags || ["domestic", "cod"]).map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-2 text-secondary fw-medium text-capitalize"
                        style={{ backgroundColor: "#f1f5f9", fontSize: "12px" }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Est. Time & Starting price info */}
                  <div className="border-top pt-3 mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">Est. Time</span>
                      <span className="fw-semibold text-dark small">{partner.estTime || "1-2 Days"}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Starting from</span>
                      <span className="fw-semibold text-dark small">{partner.starting_price || partner.startingFrom || "Rs. 60"}</span>
                    </div>
                  </div>
                </div>

                {/* Bottom Button */}
                <div>
                  {isConnected ? (
                    <button
                      type="button"
                      onClick={() => onManagePartner(partner)}
                      className="btn btn-outline-primary w-100 rounded-5 py-2 fw-semibold text-primary d-flex align-items-center justify-content-center gap-2"
                      style={{ borderColor: "#2563eb" }}
                    >
                      Manage
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onConnectPartner(partner)}
                      className="btn btn-primary w-100 rounded-5 py-2 fw-semibold border-0 text-white d-flex align-items-center justify-content-center gap-2"
                      style={{ backgroundColor: "#2563eb" }}
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default React.memo(IntegrationCatalog);
